import { classifyFailure, type FailureAction } from './classify-failure';
import type { Failure } from './failure-message';
import { isRecord, readFieldPosition } from './field-position';
import type { SubmitValidator } from './submit-validator';

/**
 * What Backstage hands a submit validator besides the value.
 *
 * @typeParam TValue - The field's value type, which Backstage's `uiSchema` type depends on.
 * @typeParam TUiOptions - The field's `ui:options`.
 */
export type ValidatorContext<
  TValue = unknown,
  TUiOptions = unknown,
> = Parameters<SubmitValidator<TValue, TUiOptions>>[2];

/**
 * Checks one submitted value.
 *
 * @typeParam TValue - The field's value type.
 * @typeParam TUiOptions - The field's `ui:options`.
 * @param value - The submitted value, untrusted: it can come from a link.
 * @param context - What Backstage hands a validator: `apiHolder`, `formData`, `schema`, `uiSchema`.
 * @returns A message for the field, or `undefined` when the value may be submitted.
 * A throw is a failed check, which {@link toSubmitValidator} classifies.
 */
export type CheckValue<TValue = unknown, TUiOptions = unknown> = (
  value: unknown,
  context: ValidatorContext<TValue, TUiOptions>,
) => Promise<string | undefined>;

/** How {@link toSubmitValidator} names the field and reads a failure. */
export interface ValidatorSettings {
  /** Describes a caught error. Without it, every failure is unknown: blocked and logged. */
  readonly describeFailure?: (error: unknown) => Failure | undefined;
  /** The name the picker is registered under, such as `AzureResourceGroupPicker`. It finds the picker inside a list item. */
  readonly fieldName: string;
  /** Where `console.error` says the failure came from, such as `entra-pickers groupValidator`. */
  readonly logPrefix: string;
  /** Proxy refusal codes a filler cannot fix by trying again. Empty when absent. */
  readonly permanentRefusalCodes?: ReadonlySet<string>;
  /** The words for the selection, such as `group`, in "Choose a group." and in the fixed message. */
  readonly subject: string;
}

const NO_REFUSAL_CODES: ReadonlySet<string> = new Set();

/** RJSF fills an untouched required object field with `{}`: that is no choice, not a broken one. */
function isEmptyObject(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  );
}

/** The message for the template owner when the picker sits where it cannot be checked. */
function describeUncheckablePosition(subject: string): string {
  return `This template uses the ${subject} picker in a way it cannot check. Contact the template owner.`;
}

/** The schema Backstage hands a validator, derived so picker-shared needs no `@backstage/types`. */
type FieldSchema = ValidatorContext['schema'];

function isFieldSchema(value: unknown): value is FieldSchema {
  return isRecord(value);
}

/** The context of one field of a list item: that field's `uiSchema` and schema. */
function fieldContext<TValue, TUiOptions>(
  context: ValidatorContext<TValue, TUiOptions>,
  key: string,
): ValidatorContext<TValue, TUiOptions> {
  const fieldUiSchema: unknown = context.uiSchema?.[key];
  const properties: unknown = context.schema.properties;
  const fieldSchema: unknown = isRecord(properties)
    ? properties[key]
    : undefined;
  return {
    ...context,
    schema: isFieldSchema(fieldSchema) ? fieldSchema : {},
    uiSchema: isRecord(fieldUiSchema) ? fieldUiSchema : {},
  };
}

/**
 * How a failed check is handled. When describing or classifying the failure
 * throws, that error is logged and the failure counts as unknown, so the field
 * gets the fixed sentence and never the text of either error.
 */
function readFailure(
  error: unknown,
  settings: ValidatorSettings,
): FailureAction {
  try {
    return classifyFailure(
      settings.describeFailure?.(error),
      settings.permanentRefusalCodes ?? NO_REFUSAL_CODES,
    );
  } catch (readError) {
    console.error(
      `${settings.logPrefix}: the failure could not be read.`,
      readError,
    );
    return { kind: 'unknown' };
  }
}

/** The message for one value, or `undefined` when it may be submitted. */
async function verdictFor<TValue, TUiOptions>(
  value: unknown,
  context: ValidatorContext<TValue, TUiOptions>,
  check: CheckValue<TValue, TUiOptions>,
  settings: ValidatorSettings,
): Promise<string | undefined> {
  if (value === undefined || value === null) return undefined;
  if (isEmptyObject(value)) return `Choose a ${settings.subject}.`;
  try {
    return await check(value, context);
  } catch (error) {
    const action = readFailure(error, settings);
    if (action.kind === 'report') return action.message;
    if (action.kind === 'unknown') {
      console.error(
        `${settings.logPrefix}: the selection could not be checked.`,
        error,
      );
      return `The selected ${settings.subject} could not be checked. Try again, and contact the portal team if it keeps failing.`;
    }
    return undefined;
  }
}

/** The messages for the picker fields of one list item. */
async function verdictsForFields<TValue, TUiOptions>(
  item: unknown,
  keys: readonly string[],
  context: ValidatorContext<TValue, TUiOptions>,
  check: CheckValue<TValue, TUiOptions>,
  settings: ValidatorSettings,
): Promise<readonly (string | undefined)[]> {
  if (item === undefined || item === null) return [];
  if (!isRecord(item)) {
    console.error(`${settings.logPrefix}: a list item is not an object.`);
    return [describeUncheckablePosition(settings.subject)];
  }
  return Promise.all(
    keys.map(key =>
      verdictFor(item[key], fieldContext(context, key), check, settings),
    ),
  );
}

/**
 * Turns a value check into the validator Backstage calls.
 *
 * @remarks
 * An empty value is left to schema validation. `{}` asks for a choice. A
 * message from the check goes to the field. A throw is classified: a temporary
 * failure lets the submit through, a permanent one reports why, and an unknown
 * one blocks with a fixed sentence while the error goes to `console.error`,
 * because a Stepper message must not carry a URL or a provider diagnostic.
 *
 * A picker in a list is checked per element, and each message starts with
 * `Item <n>: `. A position the validator cannot place blocks with a message for
 * the template owner (requirements PSV13 to PSV15).
 *
 * @typeParam TValue - The field's value type.
 * @typeParam TUiOptions - The field's `ui:options`.
 * @param check - The check, which returns a message or `undefined`.
 * @param settings - The field's words and how its failures are read.
 * @returns The validator to register with the field.
 */
export function toSubmitValidator<TValue, TUiOptions = unknown>(
  check: CheckValue<TValue, TUiOptions>,
  settings: ValidatorSettings,
): SubmitValidator<TValue, TUiOptions> {
  return async (data, field, context) => {
    const value: unknown = data;
    const position = readFieldPosition(
      value,
      context.uiSchema,
      context.schema,
      settings.fieldName,
    );
    if (position.kind === 'unsupported') {
      console.error(`${settings.logPrefix}: ${position.reason}.`);
      field.addError(describeUncheckablePosition(settings.subject));
      return;
    }
    if (position.kind === 'value') {
      const message = await verdictFor(value, context, check, settings);
      if (message !== undefined) field.addError(message);
      return;
    }
    const items: readonly unknown[] = Array.isArray(value) ? value : [];
    const perItem = await Promise.all(
      items.map(item =>
        position.kind === 'list'
          ? verdictFor(item, context, check, settings).then(message => [
              message,
            ])
          : verdictsForFields(item, position.keys, context, check, settings),
      ),
    );
    perItem.forEach((messages, index) => {
      for (const message of messages) {
        if (message !== undefined)
          field.addError(`Item ${index + 1}: ${message}`);
      }
    });
  };
}
